// Finding row operational pattern (DESIGN_SYSTEM.md §10.5, WORKSPACE_COCKPIT_SPEC.md §10.2).
// One scannable line: severity, finding (title, short ID, task, origin, owner when recorded),
// asset, analyst review and last observed, with a quiet inspect action. Selection, keyboard
// focus and compact density are supported; full IDs stay in titles and accessible names.

import { type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { PanelRight } from 'lucide-react';
import { Button } from './Button.tsx';
import {
  StateLabel,
  TruthLabel,
  type OriginState,
  type ReviewState,
  type SeverityState,
} from './TruthLabel.tsx';
import { formatDate, formatDateTime, shortId } from './format.ts';
import styles from './FindingRow.module.css';

export interface FindingRowProps {
  findingId: string;
  title: string;
  severity: SeverityState;
  asset: string;
  claimOrigin: OriginState;
  reviewState: ReviewState;
  owner?: string;
  updatedAt?: string;
  summary?: string;
  lane?: 'DNS' | 'Network' | 'Mixed';
  isSelected?: boolean;
  density?: 'comfortable' | 'compact';
  onSelect?: (findingId: string) => void;
  onInspect?: (findingId: string) => void;
  /** Row action beside Inspect, e.g. Create engagement or Open engagement. */
  action?: ReactNode;
}

export function FindingRow({
  findingId,
  title,
  severity,
  asset,
  claimOrigin,
  reviewState,
  owner,
  updatedAt,
  summary,
  lane,
  isSelected = false,
  density = 'comfortable',
  onSelect,
  onInspect,
  action,
}: FindingRowProps) {
  const handleRowClick = (e: MouseEvent<HTMLDivElement>) => {
    // If a button or interactive child was clicked, don't trigger row selection
    if ((e.target as HTMLElement).closest('button, a')) {
      return;
    }
    onSelect?.(findingId);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Don't trigger if target is an interactive child
    if ((e.target as HTMLElement).closest('button, a')) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.(findingId);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'j' || e.key === 'k') {
      // Arrow keys (or j/k) move between sibling rows so a list reads like a grid.
      const rows = Array.from(
        e.currentTarget.parentElement?.querySelectorAll<HTMLElement>('[role="row"]') ?? [],
      );
      const forward = e.key === 'ArrowDown' || e.key === 'j';
      const next = rows[rows.indexOf(e.currentTarget) + (forward ? 1 : -1)];
      if (next) {
        e.preventDefault();
        next.focus();
      }
    } else if (e.key === 'o') {
      e.preventDefault();
      onSelect?.(findingId);
    } else if (e.key === 'i') {
      e.preventDefault();
      onInspect?.(findingId);
    }
  };

  const handleInspect = () => {
    onInspect?.(findingId);
  };

  return (
    <div
      role="row"
      tabIndex={0}
      aria-selected={isSelected}
      aria-label={
        lane
          ? `Finding ${findingId}: ${title}, ${severity} severity, ${lane} task`
          : `Finding ${findingId}: ${title}, ${severity} severity`
      }
      data-selected={isSelected ? 'true' : undefined}
      data-density={density === 'compact' ? 'compact' : undefined}
      data-has-action={action ? 'true' : undefined}
      className={styles.row}
      onClick={handleRowClick}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.cellSeverity} role="gridcell">
        <StateLabel kind="severity" state={severity} compact />
      </div>

      <div className={styles.cellFinding} role="gridcell">
        <p className={styles.title} title={title}>
          {title}
        </p>
        <p className={styles.meta}>
          <code className={styles.findingId} title={findingId}>
            {shortId(findingId)}
          </code>
          {lane ? <span>{lane}</span> : null}
          <TruthLabel dimension="origin" state={claimOrigin} compact bare />
          {owner ? <span>{owner}</span> : null}
        </p>
        {summary && <p className={styles.summary}>{summary}</p>}
      </div>

      <div className={styles.cellAsset} role="gridcell">
        <code className={styles.asset} title={asset}>
          {asset.replace(/^https?:\/\//, '')}
        </code>
      </div>

      <div className={styles.cellReview} role="gridcell">
        <TruthLabel dimension="review" state={reviewState} compact bare />
      </div>

      <div className={styles.cellUpdated} role="gridcell">
        {updatedAt && (
          <time className={styles.updatedAt} dateTime={updatedAt} title={formatDateTime(updatedAt)}>
            {formatDate(updatedAt)}
          </time>
        )}
      </div>

      <div className={styles.cellActions} role="gridcell">
        {action}
        {onInspect && (
          <Button
            variant="tertiary"
            size="small"
            onPress={handleInspect}
            aria-label={`Inspect finding ${findingId}`}
            className={styles.inspectBtn}
          >
            <PanelRight size={16} aria-hidden="true" />
          </Button>
        )}
      </div>
    </div>
  );
}
